import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendChatMessage, clearChatSession, type ChatHistoryMessage, type ChatProperty } from '../services/rentalChatService';
import { generateChatResponse, entitiesToSearchParams } from '../services/aiService';
import type { ChatResponse as LocalChatResponse } from '../services/aiService';

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  suggestions?: string[];
  action?: LocalChatResponse['action'];
  properties?: ChatProperty[];
}

interface ChatState {
  isOpen: boolean;
  isMinimized: boolean;
  messages: ChatMessage[];
  isTyping: boolean;
  unreadCount: number;
  useRAG: boolean;
}

interface ChatContextType extends ChatState {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  minimizeChat: () => void;
  expandChat: () => void;
  sendMessage: (content: string) => void;
  handleSuggestionClick: (suggestion: string) => void;
  handleActionClick: (action: LocalChatResponse['action']) => void;
  clearMessages: () => void;
  toggleRAGMode: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Feature flag: the AI support chat is not launched yet. While this is true the
// floating widget (and the footer "live chat" link) show a "coming soon"
// tooltip/badge instead of opening the bot. Set VITE_CHAT_COMING_SOON=false in
// the web env to re-enable the live bot — reversible, no code change needed.
export const CHAT_COMING_SOON = import.meta.env.VITE_CHAT_COMING_SOON !== 'false';

const STORAGE_KEY = 'safaqat24_chat_messages';
const MAX_STORED_MESSAGES = 50;

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation('chat');
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<ChatState>({
    isOpen: false,
    isMinimized: false,
    messages: [],
    isTyping: false,
    unreadCount: 0,
    useRAG: true, // Default to RAG mode
  });

  // Load messages from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const messages = parsed.map((msg: ChatMessage) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
        setState(prev => ({ ...prev, messages }));
      }
    } catch (error) {
      console.error('Failed to load chat messages:', error);
    }
  }, []);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (state.messages.length > 0) {
      try {
        const toStore = state.messages.slice(-MAX_STORED_MESSAGES);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
      } catch (error) {
        console.error('Failed to save chat messages:', error);
      }
    }
  }, [state.messages]);

  // Add welcome message when chat is first opened and has no messages
  const addWelcomeMessage = useCallback(() => {
    const lang = i18n.language === 'ar' ? 'ar' : 'en';
    const welcomeMessage: ChatMessage = {
      id: generateId(),
      content: lang === 'ar'
        ? 'مرحباً! أنا المساعد الذكي لموقع صفقات 24. يمكنني مساعدتك في:\n• البحث عن العقارات المناسبة\n• الإجابة على استفساراتك عن الأسعار والمواقع\n• حجز استشارة مع خبرائنا\n\nكيف يمكنني مساعدتك اليوم؟'
        : "Welcome! I'm the AI assistant for Safaqat 24. I can help you with:\n• Finding the right properties\n• Answering your questions about prices and locations\n• Booking consultations with our experts\n\nHow can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
      suggestions: lang === 'ar'
        ? ['البحث عن شقة', 'فلل في الرياض', 'أسعار الإيجار', 'حجز استشارة']
        : ['Search apartments', 'Villas in Riyadh', 'Rental prices', 'Book consultation'],
    };
    setState(prev => ({
      ...prev,
      messages: [...prev.messages, welcomeMessage],
    }));
  }, [i18n.language]);

  const openChat = useCallback(() => {
    // Bot not launched yet: send "contact support" actions to the contact page
    // instead of opening an empty panel. Floating widget/footer are handled in
    // their own components; this covers in-page "Contact Support" buttons.
    if (CHAT_COMING_SOON) {
      navigate('/contact');
      return;
    }
    setState(prev => {
      const newState = { ...prev, isOpen: true, isMinimized: false, unreadCount: 0 };
      if (prev.messages.length === 0) {
        setTimeout(() => addWelcomeMessage(), 300);
      }
      return newState;
    });
  }, [addWelcomeMessage, navigate]);

  const closeChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const toggleChat = useCallback(() => {
    setState(prev => {
      if (!prev.isOpen) {
        const newState = { ...prev, isOpen: true, isMinimized: false, unreadCount: 0 };
        if (prev.messages.length === 0) {
          setTimeout(() => addWelcomeMessage(), 300);
        }
        return newState;
      }
      return { ...prev, isOpen: false };
    });
  }, [addWelcomeMessage]);

  const minimizeChat = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: true }));
  }, []);

  const expandChat = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: false, unreadCount: 0 }));
  }, []);

  const toggleRAGMode = useCallback(() => {
    setState(prev => ({ ...prev, useRAG: !prev.useRAG }));
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    const uiLang = i18n.language === 'ar' ? 'ar' : 'en';

    // Add user message
    const userMessage: ChatMessage = {
      id: generateId(),
      content: content.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isTyping: true,
    }));

    // Get conversation history for RAG
    const history: ChatHistoryMessage[] = state.messages
      .slice(-10)
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
        content: msg.content,
      }));

    // Add current message to history
    history.push({ role: 'user', content: content.trim() });

    try {
      if (state.useRAG) {
        // Use RAG-based backend API
        const response = await sendChatMessage({
          message: content.trim(),
          history: history.slice(0, -1), // Don't include the current message in history
          currentUrl: location.pathname,
          preferredLanguage: uiLang,
        });

        if (response.success && response.data) {
          const botMessage: ChatMessage = {
            id: generateId(),
            content: response.data.message,
            sender: 'bot',
            timestamp: new Date(),
            suggestions: response.data.suggestions,
            action: response.data.action,
            properties: response.data.properties,
          };

          setState(prev => ({
            ...prev,
            messages: [...prev.messages, botMessage],
            isTyping: false,
            unreadCount: prev.isMinimized ? prev.unreadCount + 1 : 0,
          }));
        } else {
          // RAG API failed, show error message
          const errorMessage: ChatMessage = {
            id: generateId(),
            content: uiLang === 'ar'
              ? (response.errorAr || response.error || 'حدث خطأ. يرجى المحاولة مرة أخرى.')
              : (response.error || 'An error occurred. Please try again.'),
            sender: 'bot',
            timestamp: new Date(),
            suggestions: uiLang === 'ar'
              ? ['حاول مرة أخرى', 'تواصل معنا']
              : ['Try again', 'Contact us'],
          };

          setState(prev => ({
            ...prev,
            messages: [...prev.messages, errorMessage],
            isTyping: false,
          }));
        }
      } else {
        // Fallback to local AI service (rule-based)
        const thinkingDelay = 800 + Math.random() * 700;

        setTimeout(() => {
          const response = generateChatResponse(content, uiLang);

          const botMessage: ChatMessage = {
            id: generateId(),
            content: response.message,
            sender: 'bot',
            timestamp: new Date(),
            suggestions: response.suggestions,
            action: response.action,
          };

          setState(prev => ({
            ...prev,
            messages: [...prev.messages, botMessage],
            isTyping: false,
            unreadCount: prev.isMinimized ? prev.unreadCount + 1 : 0,
          }));
        }, thinkingDelay);
      }
    } catch (error) {
      console.error('Chat error:', error);

      // Fallback to local service on network error
      const thinkingDelay = 500;

      setTimeout(() => {
        const response = generateChatResponse(content, uiLang);

        const botMessage: ChatMessage = {
          id: generateId(),
          content: response.message,
          sender: 'bot',
          timestamp: new Date(),
          suggestions: response.suggestions,
          action: response.action,
        };

        setState(prev => ({
          ...prev,
          messages: [...prev.messages, botMessage],
          isTyping: false,
          unreadCount: prev.isMinimized ? prev.unreadCount + 1 : 0,
        }));
      }, thinkingDelay);
    }
  }, [i18n.language, location.pathname, state.messages, state.useRAG]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    sendMessage(suggestion);
  }, [sendMessage]);

  const handleActionClick = useCallback((action: LocalChatResponse['action']) => {
    if (!action) return;

    switch (action.type) {
      case 'navigate':
        if (typeof action.payload === 'string') {
          navigate(action.payload);
          closeChat();
        }
        break;
      case 'search':
        if (action.payload && typeof action.payload === 'object') {
          const params = entitiesToSearchParams(action.payload as Record<string, unknown>);
          navigate(`/search?${params.toString()}`);
          closeChat();
        }
        break;
      case 'show_properties':
        navigate('/search');
        closeChat();
        break;
    }
  }, [navigate, closeChat]);

  const clearMessages = useCallback(() => {
    setState(prev => ({ ...prev, messages: [] }));
    localStorage.removeItem(STORAGE_KEY);
    clearChatSession();
  }, []);

  const value: ChatContextType = {
    ...state,
    openChat,
    closeChat,
    toggleChat,
    minimizeChat,
    expandChat,
    sendMessage,
    handleSuggestionClick,
    handleActionClick,
    clearMessages,
    toggleRAGMode,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
